import React, { useState, useCallback } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  FormControlLabel,
  Box,
  Typography,
  Checkbox,
  Divider,
  styled,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

const DURATIONS = [60, 120, 180, 240, 300];

const Content = ({ state }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);

  const handleExpansionChange = useCallback(() => {
    setExpanded(expanded => !expanded);
  }, []);

  return (
    <StyledRootAccordion expanded={expanded} onChange={handleExpansionChange}>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        aria-controls="panel1a-content"
        id="panel1a-header"
      >
        <Typography className="heading">{t('layouts.content')}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="h5" className="subheading">
          {t('layouts.beerInformation')}
        </Typography>
        <Box display="flex" flexDirection="column" alignItems="start">
          <FormControlLabel
            control={
              <Checkbox
                checked={state.isProducerVisible}
                name="isProducerVisible"
                onChange={state.handleCheck}
                color="primary"
              />
            }
            label={t('layouts.breweryName')}
          />

          {[4, 6, 8].includes(state.template) && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={[4, 6].includes(state.template) && state.isProducerLocationVisible}
                  onChange={state.handleCheck}
                  name="isProducerLocationVisible"
                  color="primary"
                />
              }
              label={t('layouts.breweryLocation')}
            />
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={state.isTypeVisible}
                onChange={state.handleCheck}
                color="primary"
                name="isTypeVisible"
              />
            }
            label={t('layouts.beerType')}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={state.isStyleVisible}
                onChange={state.handleCheck}
                color="primary"
                name="isStyleVisible"
              />
            }
            label={t('layouts.beerStyle')}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={state.isABVVisible}
                onChange={state.handleCheck}
                color="primary"
                name="isABVVisible"
              />
            }
            label={t('layouts.ABV')}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={state.isIBUVisible}
                onChange={state.handleCheck}
                color="primary"
                name="isIBUVisible"
              />
            }
            label={t('layouts.IBU')}
          />
          {[3, 5, 6].includes(state.template) && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={[3, 5, 6].includes(state.template) && state.areKeywordsVisible}
                  onChange={state.handleCheck}
                  color="primary"
                  name="areKeywordsVisible"
                />
              }
              label={t('layouts.keywords')}
            />
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={state.isKegLevelVisible}
                onChange={state.handleCheck}
                color="primary"
                name="isKegLevelVisible"
              />
            }
            label={t('layouts.kegLevel')}
          />
          {state.isKegLevelVisible && (
            <Box pl={2} pt={2} sx={{ width: '100%' }}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel id="pollingDuration">{t('layouts.updateFrequency')}</InputLabel>
                <Select
                  labelId="pollingDuration"
                  id="pollingDuration"
                  value={state.pollingDuration}
                  onChange={state.handleChange}
                  label={t('layouts.updateFrequency')}
                  name="pollingDuration"
                >
                  {DURATIONS.map((val, index) => (
                    <MenuItem key={index} value={val}>
                      {val} {t('layouts.seconds')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>
        <Divider />
        <Typography variant="h5" className="subheading">
          {t('layouts.servingSizes')}
        </Typography>
        <Box display="flex" flexDirection="column" alignItems="start">
          <FormControlLabel
            control={
              <Checkbox
                checked={state.areContainersVisible}
                onChange={state.handleCheck}
                color="primary"
                name="areContainersVisible"
              />
            }
            label={t('layouts.containers')}
          />
          <FormControlLabel
            disabled={!state.areContainersVisible}
            control={
              <Checkbox
                checked={state.isPriceVisible}
                onChange={state.handleCheck}
                color="primary"
                name="isPriceVisible"
              />
            }
            label={t('layouts.price')}
          />
          <Box display="flex" flexDirection="column" ml={2}>
            <FormControlLabel
              disabled={!state.areContainersVisible || !state.isPriceVisible}
              control={
                <Checkbox
                  checked={state.isCurrencyVisible && state.isPriceVisible}
                  onChange={state.handleCheck}
                  color="primary"
                  name="isCurrencyVisible"
                />
              }
              label={t('layouts.currency')}
            />
            <FormControlLabel
              disabled={!state.areContainersVisible || !state.isPriceVisible}
              control={
                <Checkbox
                  checked={state.roundNumbers && state.isPriceVisible}
                  onChange={state.handleCheck}
                  color="primary"
                  name="roundNumbers"
                />
              }
              label={t('layouts.roundNumbers')}
            />
          </Box>
        </Box>
      </AccordionDetails>
      <Divider />
    </StyledRootAccordion>
  );
};

export default observer(Content);

const StyledRootAccordion = styled(Accordion)(({ theme }) => ({
  '.heading': {
    fontSize: theme.typography.pxToRem(22),
    fontWeight: theme.typography.fontWeightRegular,
    '& > .MuiIconButton-edgeEnd': {
      marginRight: '0 !important',
    },
  },
  '.subheading': {
    textDecoration: 'underline',
    marginBottom: theme.spacing(1),
  },
}));
